/* eslint-disable @typescript-eslint/no-unused-vars */
import { useBexApi } from '@shared-ui/bex-api-context';

// GraphQl Types
import {
  DateInput,
  LodgingPwaPropertySearchQueryVariables,
  PaymentType,
  Points,
  PropertySort,
  TravelerType,
} from 'src/common/__generated__/api/types';

// Hooks
import useLodgingPwaPropertySearch from '../hooks/useLodgingPwaPropertySearch';

// Helper
import { checkValue } from 'src/components/common/helper';

interface HotelDataBySearchProps {
  regionName: string;
  regionId: string;
  priceMin: number;
  priceMax: number;
  sort: PropertySort;
  checkInDate: DateInput;
  checkOutDate: DateInput;
  adults: number;
  starRating: string;
  guestRating: string;
  paginationSize: number;
  travelerType?: any;
  memberOnlyDeals?: any;
  paymentType?: any;
}

interface HotelDataBySearchVrboProps {
  checkInDate: DateInput;
  checkOutDate: DateInput;
  destination: string;
  destinationId: string;
  dealSortOrder: string;
  filterByReviews4Stars: string;
  paymentType: string;
  priceMin: string;
  priceMax: string;
  adults: number;
  propertyCount: number;
}

/**
 *
 * @param props HotelDataBySearchProps
 * @returns
 */
const HotelDataBySearch = (props: HotelDataBySearchProps) => {
  const { context } = useBexApi();
  const {
    regionName,
    regionId,
    priceMin,
    priceMax,
    checkInDate,
    checkOutDate,
    adults,
    starRating,
    guestRating,
    sort,
    paginationSize = 24,
    travelerType,
    memberOnlyDeals,
    paymentType,
  } = props;
  const cmsStarRating = checkValue(starRating) ? parseInt(starRating?.replace(' stars', '0')) : 30;
  const starFilter = () => {
    const starRatings = [];
    for (let i = cmsStarRating; i < 60; i += 10) {
      starRatings.push(Number(i));
    }
    return starRatings;
  };
  const minGuestRating = checkValue(guestRating) ? `${guestRating}.0` : '4.0';
  const travelerTypes =
    checkValue(travelerType) && ['BUSINESS', 'FAMILY', 'LGBT', 'ROMANTIC'].includes(travelerType)
      ? [TravelerType[travelerType]]
      : null;
  const memberOnlyDealsFilter =
    checkValue(memberOnlyDeals) && memberOnlyDeals?.toLowerCase() === 'true' ? ['MEMBER_ONLY'] : null;
  const paymentTypes =
    checkValue(paymentType) &&
    [
      'COUPON_ELIGIBLE',
      'FREE_CANCELLATION',
      'GIFT_CARD',
      'PAY_LATER',
      'PAY_LATER_WITH_DEPOSIT',
      'PAY_WITHOUT_CREDITCARD',
    ].includes(paymentType)
      ? [PaymentType[paymentType]]
      : null;

  const lodgingPwaPropertySearchVariables: LodgingPwaPropertySearchQueryVariables = {
    context,
    destination: {
      regionName: regionName,
      regionId: regionId,
      pinnedPropertyId: null,
      propertyIds: null,
      mapBounds: null,
    },
    filters: {
      starList: starFilter(),
      structureTypes: null,
      propertyName: null,
      neighborhood: regionId,
      mealPlan: null,
      amenities: null,
      accessibility: null,
      travelerType: travelerTypes,
      reviewScore: {
        min: Number(`${minGuestRating}`),
        max: Number('5.0'),
      },
      poi: null,
      paymentType: paymentTypes,
      cleaningAndSafetyPractices: null,
      deals: null,
      propertyStyles: null,
      rewards: memberOnlyDealsFilter,
      bedroomFilter: [],
      price: {
        min: Number(priceMin),
        max: Number(priceMax),
      },
      commissionTiers: null,
      agencyBusinessModels: null,
      chain: null,
      stayTypes: null,
    },
    legacyCriteria: {
      sort: sort,
      stars: starFilter().join(','),
      priceBuckets: [Number(priceMin), Number(priceMax)],
      amenities: [],
      structureTypes: [],
      guestRating: `${minGuestRating}, 5.0`,
      paymentType: [],
    },
    rooms: [
      {
        adults: Number(adults),
        children: [],
      },
    ],
    propertyShopOptions: {
      points: Points.SHOP_WITH_POINTS,
    },
    shoppingContext: {
      multiItem: null,
    },
    sort: sort,
    dateRange: {
      checkInDate: checkInDate,
      checkOutDate: checkOutDate,
    },
    searchPagination: { size: paginationSize },
    searchIntent: {
      semdtl: null,
      theme: null,
      userIntent: null,
    },
    shouldFetchSortAndFilter: true,
    returnPropertyType: true,
  };

  const { data, error, loading } = useLodgingPwaPropertySearch(lodgingPwaPropertySearchVariables);

  return { data, error, loading };
};

/**
 *
 * @param props HotelDataBySearchVrbpProps
 * @returns
 */
const HotelDataBySearchVrbo = (props: HotelDataBySearchVrboProps) => {
  const { context } = useBexApi();

  const {
    checkInDate,
    checkOutDate,
    propertyCount = 12,
    destination,
    destinationId,
    dealSortOrder = 'RECOMMENDED',
    filterByReviews4Stars = 'False',
    paymentType = '',
    adults = 2,
    priceMin,
    priceMax,
  } = props;

  const selectionFilter = [
    {
      id: 'sort',
      value: 'RECOMMENDED',
    },
    {
      id: 'privacyTrackingState',
      value: 'CAN_TRACK',
    },
    {
      id: 'useRewards',
      value: 'SHOP_WITHOUT_POINTS',
    },
  ];

  if (checkValue(filterByReviews4Stars) && filterByReviews4Stars === 'True') {
    selectionFilter.push({
      id: 'property_reviews_group',
      value: '4_stars',
    });
  }

  if (checkValue(paymentType) && paymentType.toUpperCase() === 'FREE_CANCELLATION') {
    selectionFilter.push({
      id: 'free_cancellation_group',
      value: 'free_cancellation',
    });
  }

  const isNumber = (value) => typeof value === 'number';
  const priceFilter =
    checkValue(priceMin) && isNumber(Number(priceMin)) && checkValue(priceMax) && isNumber(Number(priceMax))
      ? [
          {
            id: 'total_price',
            min: Number(priceMin),
            max: Number(priceMax),
          },
        ]
      : [];

  const lodgingPwaPropertySearchVariables: LodgingPwaPropertySearchQueryVariables = {
    context,
    criteria: {
      primary: {
        dateRange: {
          checkInDate,
          checkOutDate,
        },
        destination: {
          regionName: destination,
          regionId: '',
          coordinates: null,
          pinnedPropertyId: null,
          propertyIds: null,
          mapBounds: null,
        },
        rooms: [
          {
            adults: Number(adults),
            children: [],
          },
        ],
      },
      secondary: {
        counts: [],
        booleans: [],
        selections: selectionFilter,
        ranges: priceFilter,
      },
    },
    destination: {
      regionName: null,
      regionId: null,
      coordinates: null,
      pinnedPropertyId: null,
      propertyIds: null,
      mapBounds: null,
    },
    shoppingContext: {
      multiItem: null,
    },
    // searchPagination: { size: propertyCount },
    returnPropertyType: false,
  };

  const { data, error, loading } = useLodgingPwaPropertySearch(lodgingPwaPropertySearchVariables);

  return { data, error, loading };
};

export { HotelDataBySearch, HotelDataBySearchVrbo, HotelDataBySearchProps, HotelDataBySearchVrboProps };
